import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as moment from 'moment';
import { Parser } from 'expr-eval';
import { ScreenPoint } from '../types/map.types';
import { BidService } from './bid.service';
import { isIssueStatusOpen, issueStage } from '../helpers/issue-workflow';

/**
 * Popup item types
 */
export type PopupItemType =
  | 'picture'
  | 'bid_item'
  | 'activity'
  | 'delivery'
  | 'forms'
  | 'labor_equipment'
  | 'quality'
  | 'issue'
  | 'multi_item';

/**
 * Summary section in popup
 */
export interface PopupSummary {
  left?: {
    label: string;
    value: number | string;
    statistics?: string;
  };
  right?: {
    label: string;
    value: number | string;
    statistics?: string;
  };
}

/**
 * Popup content structure matching existing design
 */
export interface PopupContent {
  type: PopupItemType | string;
  title: string;
  heading?: string;
  subheading1?: string;
  subheading2?: string;
  summary?: PopupSummary;
  description?: string;
  more_link_text?: string;
  thumbnail_url?: string;
  data?: {
    markerData?: any;
    type?: string;
    geometry?: any;
    screenPoint?: ScreenPoint;
    [key: string]: any;
  };
}

/**
 * Popup position configuration
 */
export interface PopupPosition {
  screenPoint: ScreenPoint;
  anchor?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  offset?: { x: number; y: number };
}

/**
 * Picture popup data
 */
export interface PicturePopupData {
  uuid: string;
  thumbnail_url: string;
  created_at?: string;
  direction?: number;
  site_id?: string;
  daily_report_id?: string;
  source_type?: string;
}

/**
 * Bid item popup data
 */
export interface BidItemPopupData {
  bid_item_id: string;
  item_name: string;
  description?: string;
  quantity: number;
  total_quantity: number;
  uom: string;
  site_id?: string;
  daily_report_id?: string;
}

/**
 * Activity popup data
 */
export interface ActivityPopupData {
  activity_id: string;
  name: string;
  description?: string;
  hours?: number;
  site_id?: string;
  daily_report_id?: string;
}

/**
 * Delivery popup data
 */
export interface DeliveryPopupData {
  delivery_id: string;
  material_name: string;
  quantity: number;
  uom: string;
  site_id?: string;
  daily_report_id?: string;
}

/**
 * MapPopupService
 *
 * Provides methods for creating and managing map popup content.
 * Centralizes popup data generation for consistent formatting.
 *
 * Part of Phase 2.4 of the map maintainability refactor.
 */
@Injectable({ providedIn: 'root' })
export class MapPopupService {
  // Headings list for bid item rollup calculations (shared with MapService)
  headingsList: BehaviorSubject<any> = new BehaviorSubject<any>([]);

  constructor(private bidService: BidService) {}

  // ============================================================================
  // Popup Content Builders (New Typed Interface)
  // ============================================================================

  /**
   * Create popup content for a picture marker
   */
  createPicturePopup(data: PicturePopupData, screenPoint?: ScreenPoint): PopupContent {
    return {
      type: 'picture',
      title: 'PHOTO',
      thumbnail_url: data.thumbnail_url,
      subheading1: data.created_at
        ? this.formatDate(data.created_at)
        : undefined,
      subheading2: data.direction !== undefined
        ? `Direction: ${this.formatDirection(data.direction)}`
        : undefined,
      more_link_text: 'View photo',
      data: {
        markerData: data,
        type: 'picture',
        screenPoint
      }
    };
  }

  /**
   * Create popup content for a bid item marker
   */
  createBidItemPopup(data: BidItemPopupData, screenPoint?: ScreenPoint): PopupContent {
    const percentage = data.total_quantity > 0
      ? Math.min(100, (data.quantity / data.total_quantity) * 100)
      : 0;

    return {
      type: 'bid_item',
      title: 'BID ITEM',
      heading: data.item_name,
      subheading1: data.description,
      summary: {
        left: {
          label: 'QUANTITY',
          value: Number(percentage.toFixed(2)),
          statistics: `${this.formatNumber(data.quantity)}${data.uom} / ${this.formatNumber(data.total_quantity)}${data.uom}`
        }
      },
      description: data.description,
      more_link_text: 'See more',
      data: {
        markerData: data,
        type: 'bid_item',
        screenPoint
      }
    };
  }

  /**
   * Create popup content for an activity marker
   */
  createActivityPopup(data: ActivityPopupData, screenPoint?: ScreenPoint): PopupContent {
    return {
      type: 'activity',
      title: 'ACTIVITY',
      heading: data.name,
      subheading1: data.description,
      summary: data.hours !== undefined
        ? {
            left: {
              label: 'HOURS',
              value: this.formatNumber(data.hours),
              statistics: `${data.hours} hrs`
            }
          }
        : undefined,
      description: data.description,
      more_link_text: 'See more',
      data: {
        markerData: data,
        type: 'activity',
        screenPoint
      }
    };
  }

  /**
   * Create popup content for a delivery marker
   */
  createDeliveryPopup(data: DeliveryPopupData, screenPoint?: ScreenPoint): PopupContent {
    return {
      type: 'delivery',
      title: 'DELIVERY & USAGE',
      heading: data.material_name,
      summary: {
        left: {
          label: 'QUANTITY',
          value: this.formatNumber(data.quantity),
          statistics: `${this.formatNumber(data.quantity)} ${data.uom}`
        }
      },
      more_link_text: 'See more',
      data: {
        markerData: data,
        type: 'delivery',
        screenPoint
      }
    };
  }

  /**
   * Create popup content for a control form marker.
   *
   * Mirrors createDeliveryPopup / levelOneDeliveriesPopupData but derives its
   * display from the control form's ui_config.summary_display (mobile + web
   * forms-summary parity):
   *  - summary_display.fields -> header field values (line 1)
   *  - summary_display.section_counts -> per-section line counts (line 2)
   * The popup shows title "FORM", heading = control_form.name, and the summary
   * lines as subheading/description.
   *
   * @param markerData - The form marker data ({ ...form, control_form, ... })
   * @param screenPoint - Optional screen position
   */
  createControlFormPopup(markerData: any, screenPoint?: ScreenPoint): PopupContent {
    const summaryLines = this.buildControlFormSummaryLines(markerData);

    return {
      type: 'forms',
      title: 'FORM',
      heading: markerData?.control_form?.name,
      subheading1: summaryLines[0] || null,
      description: summaryLines[1] || '',
      more_link_text: 'See more',
      data: {
        markerData: {
          ...markerData,
          markerCategory: 'forms',
        },
        type: 'forms',
        screenPoint,
      },
    };
  }

  /**
   * Generate popup data for control form markers (legacy $event signature,
   * mirroring levelOneDeliveriesPopupData) so it can be used from the map
   * marker-click flow / ghost-location popups.
   */
  levelOneControlFormPopupData($event: any, isGhost = false): any[] {
    let category = $event?.graphic?.attributes?.category;
    let markerData = $event?.graphic?.attributes?.data;
    let geometry = $event?.graphic?.geometry;
    let screenPoint = $event?.screenPoint;

    if (isGhost) {
      category = $event.markerType;
      markerData = $event;
      geometry = null;
      screenPoint = null;
    }

    const summaryLines = this.buildControlFormSummaryLines(markerData);

    return [
      {
        type: category || 'forms',
        title: 'FORM',
        heading: markerData?.control_form?.name,
        reportDate: markerData?.reportDate,
        subheading1: summaryLines[0] || null,
        description: summaryLines[1] || '',
        more_link_text: 'See more',
        data: {
          markerData: {
            ...markerData,
            markerCategory: category || 'forms',
          },
          type: category || 'forms',
          geometry,
          screenPoint,
        },
      },
    ];
  }

  /**
   * Build the control form summary lines from ui_config.summary_display.
   * Line 1 = configured header field values, line 2 = per-section line counts.
   * Mirrors FormsSummaryComponent.summaryLines exactly.
   */
  private buildControlFormSummaryLines(markerData: any): string[] {
    const SUMMARY_SEPARATOR = ' · ';
    const controlForm = markerData?.control_form;
    const summaryDisplay = controlForm?.ui_config?.summary_display || {};
    const fieldNames: string[] = summaryDisplay?.fields || [];
    const sectionNames: string[] = summaryDisplay?.section_counts || [];
    if (!fieldNames.length && !sectionNames.length) {
      return [];
    }

    const headerFields = controlForm?.ui_config?.header?.fields || [];
    const fieldValues = markerData?.field_values || [];

    const fieldsText = fieldNames
      .map(fieldName => {
        const field = headerFields.find((f: any) => f?.name === fieldName);
        if (!field?.id) {
          return '';
        }
        const fv = fieldValues.find((v: any) => v?.id === field.id);
        return this.formatControlFormSummaryValue(fv?.value);
      })
      .filter(Boolean)
      .join(SUMMARY_SEPARATOR);

    const lineFields = controlForm?.ui_config?.lines?.fields || [];
    const lines = markerData?.lines || [];

    const sectionCountsText = sectionNames
      .map(sectionName => {
        const sectionFieldIds = lineFields
          .filter((field: any) => field?.ui_config?.section_name === sectionName)
          .map((field: any) => field.id);
        if (!sectionFieldIds.length) {
          return '';
        }
        const count = lines.filter((line: any) =>
          (line?.field_values || []).some(
            (fv: any) => sectionFieldIds.includes(fv?.id) && !!this.formatControlFormSummaryValue(fv?.value),
          ),
        ).length;
        return count > 0 ? `${sectionName} ${count}` : '';
      })
      .filter(Boolean)
      .join(SUMMARY_SEPARATOR);

    return [fieldsText, sectionCountsText].filter(Boolean);
  }

  private formatControlFormSummaryValue(value: any): string {
    if (Array.isArray(value)) {
      return value.map(v => this.formatControlFormSummaryValue(v)).filter(Boolean).join(', ');
    }
    if (value && typeof value === 'object') {
      return this.formatControlFormSummaryValue(value?.name ?? value?.value ?? value?.date ?? value?.label);
    }
    if (value === null || value === undefined) {
      return '';
    }
    return `${value}`.trim();
  }

  /**
   * Create popup content for multiple items at same location
   */
  createMultiItemPopup(
    items: PopupContent[],
    screenPoint?: ScreenPoint
  ): PopupContent[] {
    return items.map(item => ({
      ...item,
      data: {
        ...item.data,
        screenPoint
      }
    }));
  }

  // ============================================================================
  // Popup Position Helpers
  // ============================================================================

  /**
   * Calculate popup position from map event
   * @param event - Map click/pointer event
   * @param offset - Optional offset from click point
   */
  getPopupPosition(
    event: { screenPoint?: ScreenPoint },
    offset: { x: number; y: number } = { x: 0, y: -10 }
  ): PopupPosition {
    const screenPoint = event.screenPoint || { x: 0, y: 0 };

    return {
      screenPoint: {
        x: screenPoint.x + offset.x,
        y: screenPoint.y + offset.y
      },
      anchor: 'bottom',
      offset
    };
  }

  /**
   * Calculate popup position to keep it within viewport
   * @param screenPoint - Initial screen point
   * @param popupSize - Size of the popup element
   * @param viewportSize - Size of the viewport
   */
  adjustPopupPosition(
    screenPoint: ScreenPoint,
    popupSize: { width: number; height: number },
    viewportSize: { width: number; height: number }
  ): ScreenPoint {
    let { x, y } = screenPoint;

    // Adjust if popup would go off right edge
    if (x + popupSize.width / 2 > viewportSize.width) {
      x = viewportSize.width - popupSize.width / 2 - 10;
    }

    // Adjust if popup would go off left edge
    if (x - popupSize.width / 2 < 0) {
      x = popupSize.width / 2 + 10;
    }

    // Adjust if popup would go off bottom edge
    if (y + popupSize.height > viewportSize.height) {
      y = viewportSize.height - popupSize.height - 10;
    }

    // Adjust if popup would go off top edge
    if (y < 0) {
      y = 10;
    }

    return { x, y };
  }

  // ============================================================================
  // Formatting Helpers
  // ============================================================================

  /**
   * Format a date string for display
   */
  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Format a compass direction in degrees
   */
  formatDirection(degrees: number): string {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return `${Math.round(degrees)}° ${directions[index]}`;
  }

  /**
   * Format a number for display
   */
  formatNumber(value: number, decimals: number = 2): string {
    if (value === undefined || value === null || isNaN(value)) {
      return '0';
    }
    return Number(value.toFixed(decimals)).toString();
  }

  /**
   * Format a percentage value
   */
  formatPercentage(value: number, decimals: number = 1): string {
    return `${this.formatNumber(value, decimals)}%`;
  }

  // ============================================================================
  // Popup State Helpers
  // ============================================================================

  /**
   * Check if a popup should show based on marker attributes
   * Returns true if the marker has enough data to display a popup
   */
  canShowPopup(attributes: Record<string, any>): boolean {
    if (!attributes) {
      return false;
    }

    const { markerType, uuid } = attributes;

    // All marker types require at least a UUID
    if (!uuid) {
      return false;
    }

    // Type-specific validation
    switch (markerType) {
      case 'picture':
        return !!attributes.thumb_url || !!attributes.thumbnail_url;
      case 'bid_item':
        return !!attributes.bid_item_id || !!attributes.title;
      case 'activity':
        return !!attributes.activity_id || !!attributes.title;
      case 'delivery':
        return !!attributes.delivery_id || !!attributes.title;
      case 'multi_item_location':
        return true; // Always show for multi-item markers
      default:
        return true;
    }
  }

  /**
   * Get popup title based on marker type
   */
  getPopupTitle(markerType: PopupItemType | string): string {
    const titles: Record<string, string> = {
      picture: 'PHOTO',
      bid_item: 'BID ITEM',
      activity: 'ACTIVITY',
      delivery: 'DELIVERY & USAGE',
      labor_equipment: 'LABOR & EQUIPMENT',
      quality: 'QUALITY',
      issue: 'ISSUE',
      multi_item: 'ITEMS AT LOCATION'
    };

    return titles[markerType] || 'DETAILS';
  }

  /**
   * Get popup icon based on marker type
   */
  getPopupIcon(markerType: PopupItemType | string): string {
    const icons: Record<string, string> = {
      picture: 'photo_camera',
      bid_item: 'receipt_long',
      activity: 'engineering',
      delivery: 'local_shipping',
      labor_equipment: 'groups',
      quality: 'verified',
      issue: 'warning',
      multi_item: 'layers'
    };

    return icons[markerType] || 'info';
  }

  // ============================================================================
  // Legacy Popup Methods (Migrated from MapService)
  // These methods maintain backward compatibility during the transition.
  // New code should use the typed interface methods above.
  // ============================================================================

  /**
   * Generate popup data for bid item markers
   * @deprecated Use createBidItemPopup() for new implementations
   */
  levelOneBidItemPopupData($event: any, isGhost = false): any[] {
    let isMultilineEnable = false;
    let category = $event?.graphic?.attributes?.category;
    let markerData = $event?.graphic?.attributes?.data;
    let geometry = $event?.graphic?.geometry;
    let screenPoint = $event?.screenPoint;

    if (isGhost) {
      category = $event.markerType;
      markerData = $event;
      geometry = null;
      screenPoint = null;
    }
    let stations = undefined;
    if ($event?.graphic?.attributes?.data.child === undefined && (isGhost && markerData.child === undefined)) {
      stations = markerData.children?.filter(o => o.station);
    } else {
      isMultilineEnable = true;
    }

    // Check if child actually exists when isMultilineEnable is true
    // If child doesn't exist, fall back to using markerData directly
    const hasChild = isMultilineEnable && markerData?.child;

    // Try _getQuantity first, fall back to markerData.quantity if it fails or returns 0
    let quantity = 0;
    try {
      quantity = this._getQuantity({
        bidItem: hasChild ? markerData?.child?.bid_item : markerData?.bid_item,
        stations: (stations && stations.length > 0) ? stations : [hasChild ? markerData?.child : markerData],
        headerItem: hasChild ? markerData?.child : markerData
      });
    } catch (e) {
      // Rollup calculation failed, will use fallback
      quantity = 0;
    }

    // Fallback to direct quantity from marker data (like old code did)
    if (!quantity || quantity === 0) {
      quantity = hasChild ? (markerData?.child?.quantity || 0) : (markerData?.quantity || 0);
    }

    const bidItem = hasChild ? markerData?.child?.bid_item : markerData?.bid_item;
    const uom = bidItem?.uom || '';
    const totalQuantity = bidItem?.quantity || 0;
    const rounding = typeof bidItem?.quantity_rounding === 'number' ? bidItem.quantity_rounding : 2;

    // Apply rounding to quantities
    const roundedQuantity = Number(quantity.toFixed(rounding));
    const roundedTotalQuantity = Number(totalQuantity.toFixed(rounding));

    return [
      {
        type: category,
        title: 'BID ITEM',
        heading: markerData?.bid_item?.item,
        subheading1: markerData?.bid_item?.description,
        reportDate: markerData?.reportDate,
        ...(stations && stations.length
          ? {
            subheading2: `Stations from ${stations[0].station.name} to ${stations[stations.length - 1].station.name}`
          }
          : {}),
        summary: {
          left: {
            label: 'QUANTITY',
            value: (() => {
              try {
                let value = totalQuantity > 0 ? (quantity / totalQuantity) * 100 : 0;

                if (!value) {
                  value = 0;
                } else if (value > 100) {
                  value = 100;
                }

                value = Number(value.toFixed(2));

                return value;
              } catch (e) {
                return 0;
              }
            })(),
            statistics: `${roundedQuantity}${uom} / ${roundedTotalQuantity}${uom}`
          }
        },
        description: (() => {
          if (isMultilineEnable) {
            return markerData?.child?.comment;
          }
          const lineComments = markerData?.allLineItems
            ?.map(child => child?.comment)
            ?.filter(comment => comment && comment.trim())
            ?.join(' ');
          return lineComments || '';
        })(),
        more_link_text: 'See more',
        data: {
          markerData: {
            ...markerData,
            markerCategory: category
          },
          type: category,
          geometry,
          screenPoint
        }
      }
    ];
  }

  /**
   * Generate popup data for delivery markers
   * @deprecated Use createDeliveryPopup() for new implementations
   */
  levelOneDeliveriesPopupData($event: any, isGhost = false): any[] {
    let category = $event?.graphic?.attributes?.category;
    let markerData = $event?.graphic?.attributes?.data;
    let geometry = $event?.graphic?.geometry;
    let screenPoint = $event?.screenPoint;

    if (isGhost) {
      category = $event.markerType;
      markerData = $event;
      geometry = null;
      screenPoint = null;
    }

    return [
      {
        type: category,
        title: 'DELIVERY & USAGE',
        heading: markerData?.material?.name || markerData?.bid_item?.name,
        reportDate: markerData?.reportDate,
        subheading1: null,
        summary: {
          left: {
            label: 'TODAY',
            value: (() => {
              try {
                const { received, used } = markerData;

                if (markerData.material && received && used) {
                  let value = (used / received) * 100;

                  if (!value) {
                    value = 0;
                  } else if (value > 100) {
                    value = 100;
                  }

                  value = Number(value.toFixed(2));

                  return value;
                } else if (received && !used) {
                  return 0;
                }

                return 100;
              } catch (e) {
                return 100;
              }
            })(),
            statistics: (() => {
              try {
                let value = '';
                if (markerData.material) {
                  let { received, used } = markerData;

                  received = (received && Number(received.toFixed(2))) || 0;
                  used = (used && Number(used.toFixed(2))) || 0;

                  value = `Received: ${received} / Used: ${used}`;
                } else {
                  value = `Received: ${markerData.quantity}`;
                }

                return value;
              } catch (e) {
                return 'Received: 0 / Used: 0';
              }
            })(),
          },
          ...(markerData?.material
            ? {
              right: {
                label: 'ALL TIME',
                value: (() => {
                  const received = markerData?.material?.total_received;
                  const used = markerData?.material?.total_used;

                  try {
                    if (received && used) {
                      let value = (used / received) * 100;
                      if (!value) {
                        value = 0;
                      } else if (value > 100) {
                        value = 100;
                      }

                      value = Number(value.toFixed(2));

                      return value;
                    }

                    return 0;
                  } catch (e) {
                    return 0;
                  }
                })(),
                statistics: (() => {
                  let received = markerData?.material?.total_received;
                  let used = markerData?.material?.total_used;

                  received = (received && Number(received.toFixed(2))) || 0;
                  used = (used && Number(used.toFixed(2))) || 0;

                  return `Received: ${received} / Used: ${used}`;
                })(),
              },
            }
            : {}),
        },
        description: markerData?.comment,
        more_link_text: 'See more',
        data: {
          markerData: {
            ...markerData,
            markerCategory: category,
          },
          type: category,
          geometry,
          screenPoint,
        },
      },
    ];
  }

  /**
   * Generate popup data for activity markers
   * @deprecated Use createActivityPopup() for new implementations
   */
  levelOneActivitiesPopupData($event: any, isGhost = false): any[] {
    let category = $event?.graphic?.attributes?.category;
    let markerData = $event?.graphic?.attributes?.data;
    let geometry = $event?.graphic?.geometry;
    let screenPoint = $event?.screenPoint;

    if (isGhost) {
      category = $event.markerType;
      markerData = $event;
      geometry = null;
      screenPoint = null;
    }

    return [
      {
        type: category,
        title: 'Activities',
        heading: markerData?.title,
        reportDate: markerData?.reportDate,
        subheading1: `${markerData.pictures?.length || 0} pictures${markerData.annotations?.length ? `, ${markerData.annotations?.length} annotation(s)` : ''
          }`,
        more_link_text: 'See more',
        data: {
          markerData: {
            ...markerData,
            markerCategory: category,
          },
          type: category,
          geometry,
          screenPoint,
        },
      },
    ];
  }

  /**
   * Generate popup data for quality test markers
   */
  levelOneQualityPopupData($event: any, isGhost = false): any[] {
    let category = $event?.graphic?.attributes?.category;
    let markerData = $event?.graphic?.attributes?.data;
    let geometry = $event?.graphic?.geometry;
    let screenPoint = $event?.screenPoint;

    if (isGhost) {
      category = $event.markerType;
      markerData = $event;
      geometry = null;
      screenPoint = null;
    }

    const summary = {
      type: null,
      typeClass: null,
      icon: null,
      status: null,
      statusClass: null,
      date: null,
    };

    switch (markerData.status) {
      case 'passed':
        summary.type = 'Closed';
        summary.typeClass = 'closed';
        summary.icon = null;
        summary.status = 'Passed';
        summary.statusClass = 'passed';
        summary.date = `(${moment(markerData.updated_at).format('MMMM dd, yyyy')})`;
        break;
      case 'failed':
        summary.type = 'Closed';
        summary.typeClass = 'closed';
        summary.icon = null;
        summary.status = 'Failed';
        summary.statusClass = 'failed';
        summary.date = `(${moment(markerData.updated_at).format('MMMM dd, yyyy')})`;
        break;
      case 'sent_to_lab':
        summary.type = 'Open';
        summary.typeClass = 'open';
        summary.icon = null;
        summary.status = 'Requested';
        summary.statusClass = 'requested';
        break;
      case 'lab_scheduled':
        summary.type = 'Open';
        summary.typeClass = 'open';
        summary.icon = null;
        summary.status = 'Scheduled';
        summary.statusClass = 'scheduled';
        break;
      case 'lab_activity_completed':
        summary.type = 'Open';
        summary.typeClass = 'open';
        summary.icon = null;
        summary.status = 'Visit completed';
        summary.statusClass = 'visit-completed';
        break;
      case 'updated_report':
        summary.type = 'Open';
        summary.typeClass = 'open';
        summary.icon = null;
        summary.status = 'Results uploaded';
        summary.statusClass = 'results-uploaded';
        break;
    }

    return [
      {
        type: category,
        title: 'MATERIAL TESTING',
        heading: markerData.test_type === 'internal' ? markerData.internal_test?.name : markerData?.bid_item?.item,
        subheading1:
          markerData.test_type === 'internal' ? markerData?.bid_item?.item : markerData?.bid_item?.description,
        ...(markerData.test_type !== 'internal' ? { summary } : {}),
        description: markerData.comment,
        more_link_text: 'See more',
        data: {
          markerData: {
            ...markerData,
            markerCategory: category,
          },
          type: category,
          geometry,
          screenPoint,
        },
      },
    ];
  }

  /**
   * Generate popup data for issue markers
   */
  levelOneIssuesPopupData($event: any, isGhost = false): any[] {
    let category = $event?.graphic?.attributes?.category;
    let markerData = $event?.graphic?.attributes?.data;
    let geometry = $event?.graphic?.geometry;
    let screenPoint = $event?.screenPoint;

    if (isGhost) {
      category = $event.markerType;
      markerData = $event;
      geometry = null;
      screenPoint = null;
    }

    return [
      {
        type: category,
        title: 'ISSUES',
        heading: markerData?.title,
        subheading1: markerData?.type,
        summary: {
          // The plain open / closed badge; at a contractor-workflow stage the
          // popup shows the stage pill in its place (map-popup-type-one).
          type: markerData?.status,
          typeClass: markerData?.status,
          hideBadge: !!issueStage(markerData),
          date:
            isIssueStatusOpen(markerData?.status)
              ? moment(markerData.created_at).format('MMMM DD, YYYY')
              : moment(markerData?.closed_at).format('MMMM DD, YYYY'),
        },
        description: markerData?.description,
        more_link_text: 'See more',
        data: {
          markerData: {
            ...markerData,
            markerCategory: category,
          },
          type: category,
          geometry,
          screenPoint,
        },
      },
    ];
  }

  /**
   * Generate popup data for labor/equipment markers
   */
  levelOneLaborEquipmentPopupData($event: any, isGhost = false): any[] {
    let category = $event?.graphic?.attributes?.category;
    let popupData = $event?.graphic?.attributes?.data;
    let geometry = $event?.graphic?.geometry;
    let screenPoint = $event?.screenPoint;

    if (isGhost) {
      category = $event.markerType;
      popupData = $event;
      geometry = null;
      screenPoint = null;
    }
    return [
      {
        type: category,
        title: 'LABOR & EQUIPMENT',
        stacks: [
          {
            heading: `${popupData.employee} Employee(s), ${popupData.subcontractor} Subcontractor(s)`,
            subheading1: `${popupData.employeeStandardTime} hour(s), ${popupData.employeeOverTime} hour(s) overtime`,
            description: popupData.labor.find(o => o.is_header === true)?.comment || null,
          },
          {
            heading: `${popupData.machine} Machine(s)`,
            subheading1: `${popupData.machineStandardTime} hour(s), ${popupData.machineOverTime} hour(s) overtime`,
            description: popupData.equipment.find(o => o.is_header === true)?.comment || null,
          },
        ],
        more_link_text: 'See more',
        data: {
          markerData: {
            ...popupData,
            markerCategory: category,
          },
          type: category,
          geometry,
          screenPoint,
        },
      },
    ];
  }

  /**
   * Generate popup data for ghost (no-location) items
   */
  levelOneGhostLocationsPopupData(ghostLocations: any[]): any[] {
    let popupData = [];

    for (let i = 0; i < ghostLocations.length; i++) {
      const eachLocation = ghostLocations[i];
      const category = eachLocation.markerType;

      switch (category) {
        case 'bid_items':
          popupData = [...popupData, ...this.levelOneBidItemPopupData(eachLocation, true)];
          break;
        case 'deliveries':
          popupData = [...popupData, ...this.levelOneDeliveriesPopupData(eachLocation, true)];
          break;
        case 'forms':
          popupData = [...popupData, ...this.levelOneControlFormPopupData(eachLocation, true)];
          break;
        case 'activities':
          popupData = [...popupData, ...this.levelOneActivitiesPopupData(eachLocation, true)];
          break;
        case 'quality_test':
          popupData = [...popupData, ...this.levelOneQualityPopupData(eachLocation, true)];
          break;
        case 'issues':
          popupData = [...popupData, ...this.levelOneIssuesPopupData(eachLocation, true)];
          break;
        case 'labor_equipment':
          popupData = [...popupData, ...this.levelOneLaborEquipmentPopupData(eachLocation, true)];
          break;
      }
    }

    return popupData;
  }

  /**
   * Generate popup data for grouped location markers
   */
  levelOneGroupedLocationsPopupData($event: any): any[] {
    const children = $event?.graphic?.attributes?.data?.children;
    return this.levelOneGhostLocationsPopupData(children);
  }

  /**
   * Get tooltip content for a marker
   */
  getTooltipContentLegacy(data: any): string | undefined {
    switch (data.markerCategory) {
      case 'activities':
        return 'Activities';
      case 'bid_items':
        if (data.level >= 2) {
          return `${data.bid_item.item}${data.station ? `- (${data.station?.name})` : ''}`;
        }
        return data.bid_item.item;
      case 'issues':
        return data.bid_item.item;
      case 'alignment':
        return data.name;
      case 'quality_test':
        if (data.test_type === 'internal') {
          return data.internal_test.name || 'Onsite Tests';
        }

        if (data.test_type === 'external') {
          return data.external_test.name || 'Offsite Tests';
        }

        break;
      case 'labor_equipment':
        if (data.labor) {
          return `${data.labor.first_name} ${data.labor.last_name}`;
        }

        if (data.equipment) {
          return `${data.equipment.description ? `${data.equipment.description} - ` : ''}${data.equipment.make}`;
        }

        return 'Labor & Equipment';
      case 'deliveries':
        return data?.material?.name || 'Material Delivery';
      default:
        return undefined;
    }
  }

  // ============================================================================
  // Private Helper Methods (for quantity calculations)
  // ============================================================================

  private _getQuantity(params: { bidItem: any; stations: any[]; headerItem: any }): number {
    const { bidItem, stations, headerItem } = params;

    if (!bidItem?.rollup) {
      // If stations[0] has children, sum the quantities of the children; otherwise, use stations[0].quantity
      if (stations[0]?.children?.length > 0) {
        return stations[0].children.reduce((sum, child) => sum + (child?.quantity || 0), 0);
      }
      return stations[0]?.quantity || 0;
    }

    if (!bidItem?.record_by_station && bidItem?.rollup) {
      // If stations[0] has children, perform _simpleRollup on each child; otherwise, use stations[0]
      if (stations[0]?.children?.length > 0) {
        const rollupValues = stations[0].children.map(child =>
          this._simpleRollup({
            rollupFormula: bidItem.rollup_formula,
            fields: this._getFields(bidItem.fields, child?.field_values || {}),
          }) || 0
        );
        // Sum up the rollup results
        return rollupValues.reduce((sum, value) => sum + value, 0);
      }

      // Default logic for stations[0]
      return this._simpleRollup({
        rollupFormula: bidItem.rollup_formula,
        fields: this._getFields(bidItem.fields, stations[0]?.field_values || {}),
      });
    }

    if (bidItem.record_by_station && bidItem.rollup) {
      const headings = this.headingsList.getValue();

      return this.bidService.getRollup(
        {
          ...bidItem,
          headings
        },
        headerItem,
        stations
      );
    }

    return 0;
  }

  private _simpleRollup(params: { rollupFormula: string; fields: any }): number {
    const { rollupFormula, fields } = params;
    return Number(Parser.evaluate(rollupFormula, fields).toFixed(2));
  }

  private _getFields(fields: any[] = [], values: any = []): Record<string, any> {
    let fieldValues: Record<string, any> = {};

    // Handle case when values is not an array (could be an object)
    if (!Array.isArray(values)) {
      return fieldValues;
    }

    values.map(o => {
      const currentField = fields.find(i => o.id === i.id);

      if (currentField) {
        fieldValues = {
          ...fieldValues,
          [currentField.name]: o.value
        };
      }
    });

    return fieldValues;
  }
}
